import { omit } from "lodash"
import createModule from "../createModule"
import view from "./view"
import { getOptimizeDate } from "modules/optimization/selectors"
import { getViewType, getSelectedModelId, getPortfolioFxHedgeEnabled } from "modules/portfolio-root/selectors/portfolios"
import { getBasePositionSet, getBaseSignature } from "selectors/base"
import { Creators } from "./model"
import { OK } from "helpers/data-statuses"
import { getForecast, getOriginalForecast } from "modules/optimization/selectors/data"
import { getCurrentHorizon, getSelectedCustomForecast } from "modules/optimization/selectors/constraints"
import { getBestPointData, getStatus as getScannerStatus } from "modules/optimization-scanner/selectors/data"
import { getOriginalRiskDecomposition, getRebalancedRiskDecomposition, getRebalancedForecast } from "./selectors/data"
import { getCategories, getSelectedFactors } from "./selectors/categories"
import { getDecompositionTypes, getSelectedDecompositionTypeName } from "./selectors/decomposition-types"
import { VIEW_TYPES } from "helpers/base"
import { getSortedContentSets } from "selectors/contentSets"
import { FACTOR_SENSITIVITY_TYPES, factorSensitivityParametersString } from "helpers/factorSensitivity"
import { getOptimizationContentSetsQueryNodes } from "helpers/contentSet"
import { MODEL_METADATA_QUERY } from "resources/models"

export default createModule({
  view,
  externalProps: state => ({
    portfolioId: state.global.selected.portfolio,
    account: state.global.selected.account,
    modelId: getSelectedModelId(state),
    selectedDate: getOptimizeDate(state),
    forecast: getForecast(state),
    bestPointData: getBestPointData(state),
    scannerStatus: getScannerStatus(state),
    base: getBasePositionSet(state),
    baseSignature: getBaseSignature(state),
    viewType: getViewType(state),
    horizon: getCurrentHorizon(state),
    selectedCustomForecast: getSelectedCustomForecast(state),
    contentSets: getSortedContentSets(state),
    fxHedgeEnabled: getPortfolioFxHedgeEnabled(state),
  }),
  required: ["account", "portfolioId", "modelId", "selectedDate", "riskTarget", "forecast", "bestPointData", "horizon"],
  data: [{
    resource: "scorecardData",
    as: null,
    url: ({ modelId, portfolioId, selectedDate, riskTarget, forecast, bestPointData, scannerStatus, constraints, viewType, base, baseSignature, selectedCustomForecast, contentSets, fxHedgeEnabled }) => {
      if (scannerStatus === OK && bestPointData) {
        baseSignature = viewType === VIEW_TYPES.ACTIVE ? baseSignature : ""
        base = viewType === VIEW_TYPES.ACTIVE ? base : null

        const objective = [{ targetTotalRisk: riskTarget.predictedTotalRisk / 100 }]
        forecast = omit(forecast, "total")

        return {
          id: `scorecardData-${modelId}${portfolioId}${selectedDate}${selectedCustomForecast || "implied-return"}${JSON.stringify(objective)}${JSON.stringify(constraints)}${baseSignature}${fxHedgeEnabled}`,
          query: `query ($modelId: String!, $portfolioId: String!, $on: Date!, $objective: [OptimizationObjective]!, $constraints: OptimizationConstraints!, $forecast: ForecastInput, $base: [PositionSetInput]) {
            model (id: $modelId) {
              optimization (
                positionSet: {
                  type: PORTFOLIO,
                  id: $portfolioId
                },
                base: $base,
                on: [$on],
                constraints: $constraints,
                objective: $objective,
                forecast: $forecast
              ) {
                positionsDelta {
                  equities {
                    id { ticker mic modelProviderId bloombergTicker internalId }
                    economicExposure
                  }
                  swaps { id economicExposure }
                }
                positions {
                  dates {
                    equities {
                      id {
                        ticker
                        mic
                        modelProviderId
                        bloombergTicker
                        figi
                        internalId
                      }
                      economicExposure
                    }
                    currencies { id economicExposure }
                    swaps { id economicExposure }
                    fixedIncome { id { isin } economicExposure }
                    commodities { id economicExposure }
                    otherAssets { id economicExposure }
                  }
                }
                exposure {
                  factors {
                    id
                    value: net
                    category
                  }
                }
                risk {
                  date
                  total
                  attribution {
                    summary {
                      factors
                      specific
                    }
                    factors {
                      id
                      category
                      value
                    }
                  }
                }
                predictedFactorSensitivity: factorSensitivity(${factorSensitivityParametersString(FACTOR_SENSITIVITY_TYPES.PREDICTED)}) {
                  factors {
                    id
                    category
                    value: sensitivity
                  }
                }
                historicalFactorSensitivity: factorSensitivity(${factorSensitivityParametersString(FACTOR_SENSITIVITY_TYPES.HISTORICAL)}) {
                  factors {
                    id
                    category
                    value: sensitivity
                  }
                }
                ${getOptimizationContentSetsQueryNodes(contentSets)}
              }
            }
          }`,
          variables: {
            modelId,
            portfolioId,
            constraints,
            on: selectedDate,
            objective,
            forecast,
            base,
          },
        }
      }

      return null
    },
  },
  MODEL_METADATA_QUERY,
  ],
  mapStateToProps: (state, props) => ({
    ...state.modules.optimizationScorecard,
    forecast: getOriginalForecast(state),
    rebalancedForecast: getRebalancedForecast(state),
    originalRiskDecomposition: getOriginalRiskDecomposition(state),
    rebalancedRiskDecomposition: getRebalancedRiskDecomposition(state),
    factorCategories: getCategories(state, props),
    selectedFactors: getSelectedFactors(state, props),
    decompositionTypes: getDecompositionTypes(state, props),
    selectedDecompositionTypeName: getSelectedDecompositionTypeName(state, props),
  }),
  actions: Creators,
})
